/**
 * Every answer in an exercise, one entry per answer.
 *
 * Until now a WRONG answer left no trace at all. `practisedAt` on a word and
 * `solved` on a photo finding both remember only THAT it was answered
 * correctly in the end — which is what the exercise needs in order to stop
 * asking, and it is all it needs.
 *
 * It says nothing about how securely a mistake kind sits. And that is the
 * number worth showing: a kind can have been the most frequent mistake and
 * sit perfectly well by now. Counting where the mistakes came from would go
 * on calling it the biggest problem long after it stopped being one.
 *
 * One entry per answer, not a counter per kind: only so can the course over
 * weeks be worked out. Entries are never changed, only added — which makes
 * the merge the simplest one there is, the same as for the stock catalogue.
 */

const KEY = 'kritzl.answers'

/** Forgets every answer. For a device that changes hands. */
export function forgetAnswers(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {}
}

/** How many answers are kept. Four weeks of daily practice fit easily. */
const MAX = 2000

/** One answer, as it is stored and as the sync carries it. */
export interface Answer {
  /** The id of the exercise: `w-fare`, `foto-…`, `stock-42`. */
  id: string
  at: number
  /** The mistake kind. The evaluation runs over kinds, not over words. */
  kind: string
  ok: boolean
}

/** Everything answered so far, oldest first. */
export function answers(): Answer[] {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return []
    const list = JSON.parse(raw) as unknown
    if (!Array.isArray(list)) return []
    return list.filter(isAnswer).sort((a, b) => a.at - b.at)
  } catch {
    // Private mode, blocked storage, a broken entry — the evaluation then
    // shows nothing, and practising works all the same.
    return []
  }
}

function isAnswer(x: unknown): x is Answer {
  if (!x || typeof x !== 'object') return false
  const a = x as Partial<Answer>
  return typeof a.id === 'string' && typeof a.at === 'number' &&
    typeof a.kind === 'string' && typeof a.ok === 'boolean'
}

/**
 * Writes the answers, keeping only the youngest `MAX`.
 *
 * @returns true when it really got written.
 */
function write(list: readonly Answer[]): boolean {
  const keep = [...list].sort((a, b) => a.at - b.at).slice(-MAX)
  try {
    localStorage.setItem(KEY, JSON.stringify(keep))
    return true
  } catch {
    return false
  }
}

/**
 * Books one answer.
 *
 * Both the right and the wrong ones — the wrong ones are the whole point of
 * this store.
 */
export function addAnswer(id: string, kind: string, ok: boolean): void {
  if (!id) return
  write([...answers(), { id, at: Date.now(), kind, ok }])
}

/**
 * Takes over what another device has answered.
 *
 * Two entries are the same when id AND time match: the same exercise answered
 * twice is two answers, and whoever gets it right on the second go must not
 * lose the first, wrong one.
 *
 * @returns true when something new arrived.
 */
export function mergeAnswers(incoming: readonly unknown[]): boolean {
  const here = answers()
  const known = new Set(here.map((a) => `${a.id}@${a.at}`))
  const added = incoming.filter(isAnswer).filter((a) => !known.has(`${a.id}@${a.at}`))
  if (!added.length) return false
  // Only report a change when one was really stored. With blocked storage the
  // read comes back empty every time, so everything would look new on every
  // sync — and the screen would be redrawn for nothing, again and again.
  return write([...here, ...added])
}

/**
 * How many answers a kind needs before the bar can fill.
 *
 * The bar says how well something sits, and after one right answer nothing
 * sits yet. Two is what it counts as already-wrong: three right answers then
 * read as 60 %, twenty as 91 %. It never quite reaches the top, which is
 * honest — nobody is ever entirely sure of a rule.
 */
const RUN_UP = 2

/** How one mistake kind is doing. */
export interface KindRate {
  kind: string
  /** How many answers were given on it in the window. */
  total: number
  /** How many of them were wrong. */
  wrong: number
  /** The share of wrong ones, 0 to 100. */
  rate: number
  /**
   * How well the kind sits, 0 to 100 — the share of right answers, damped by
   * `RUN_UP`. This is what the screen shows: a bar that grows as one gets
   * better reads differently from one that measures failure.
   */
  mastery: number
}

/**
 * The error rate per mistake kind, over a window of time.
 *
 * @param from  Only answers from this moment on. Omitted: everything.
 * @param to    Only answers up to this moment. Omitted: up to now.
 */
export function ratesByKind(from = 0, to = Infinity, list = answers()): KindRate[] {
  const by = new Map<string, { total: number; wrong: number }>()
  for (const a of list) {
    if (a.at < from || a.at > to || !a.kind) continue
    const e = by.get(a.kind) ?? { total: 0, wrong: 0 }
    e.total += 1
    if (!a.ok) e.wrong += 1
    by.set(a.kind, e)
  }
  return [...by.entries()]
    .map(([kind, e]) => ({
      kind,
      total: e.total,
      wrong: e.wrong,
      rate: Math.round((e.wrong / e.total) * 100),
      mastery: Math.round(((e.total - e.wrong) / (e.total + RUN_UP)) * 100),
    }))
    .sort((a, b) => b.total - a.total)
}

/**
 * The error rate per kind over the last `weeks` weeks, oldest week first.
 *
 * A week without an answer on a kind yields `null` for it, not a zero: a zero
 * would read as "never wrong", and a gap in the line is the honest picture of
 * a week in which the kind was not practised at all.
 */
export function weeklyRates(
  weeks = 4,
  now = Date.now(),
  list = answers()
): Map<string, (number | null)[]> {
  const WEEK = 7 * 24 * 60 * 60 * 1000
  const out = new Map<string, (number | null)[]>()

  // Every kind that turns up at all gets a row of the full length, so the
  // lines can be drawn without checking the length at every point.
  const kinds = new Set(list.map((a) => a.kind).filter(Boolean))
  for (const k of kinds) out.set(k, Array(weeks).fill(null))

  for (let i = 0; i < weeks; i++) {
    const to = now - (weeks - 1 - i) * WEEK
    const from = to - WEEK
    for (const r of ratesByKind(from, to, list)) {
      out.get(r.kind)![i] = r.rate
    }
  }
  return out
}
